import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Utensils, Compass, Waves, Wine, UtensilsCrossed, Sailboat, Anchor } from "lucide-react";

const RESTAURANT_ICONS = [Wine, UtensilsCrossed, Sailboat, Anchor];

export default function Region() {
  const { t } = useLang();

  return (
    <section id="region" data-testid="region-section" className="py-24 md:py-32 bg-[#F0EBE1]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16 md:mb-20 max-w-2xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C48E66] mb-6" data-testid="region-eyebrow">{t.region.eyebrow}</p>
          <h2 className="font-serif text-5xl md:text-6xl text-[#112A46] font-light leading-[1.02] tracking-tight" data-testid="region-title">
            {t.region.title_1}<span className="italic">{t.region.title_italic}</span>
          </h2>
          <p className="mt-6 text-[#5C665F] text-base md:text-lg leading-relaxed">{t.region.subtitle}</p>
        </div>

        <RestaurantsGroup title={t.region.restaurants_title} items={t.region.restaurants} />

        <div className="mt-24" />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-14">
          <div className="md:col-span-6">
            <ItemsGroup icon={Compass} title={t.region.poi_title} items={t.region.poi} testid="poi" />
          </div>
          <div className="md:col-span-6">
            <ItemsGroup icon={Waves} title={t.region.activities_title} items={t.region.activities} testid="activities" />
          </div>
        </div>
      </div>
    </section>
  );
}

function RestaurantsGroup({ title, items }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7 }}
      data-testid="region-restaurants"
    >
      <div className="flex items-center gap-3 mb-10">
        <Utensils size={22} strokeWidth={1} className="text-[#112A46]" />
        <h3 className="font-serif text-3xl md:text-4xl text-[#112A46] font-light">{title}</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 md:gap-x-12 gap-y-8">
        {items.map((item, i) => {
          const Icon = RESTAURANT_ICONS[i % RESTAURANT_ICONS.length];
          const inner = (
            <>
              <Icon size={22} strokeWidth={1} className="text-[#C48E66] flex-shrink-0" />
              <span className="font-serif italic text-2xl md:text-3xl text-[#112A46] font-light group-hover/r:text-[#C48E66] transition-colors duration-300">{item.name}</span>
            </>
          );
          const cls = "group/r flex items-center justify-center gap-4 py-2";
          return item.url ? (
            <a
              key={i}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`region-restaurants-item-${i}`}
              className={cls}
            >
              {inner}
            </a>
          ) : (
            <div
              key={i}
              data-testid={`region-restaurants-item-${i}`}
              className={cls}
            >
              {inner}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function ItemsGroup({ icon: Icon, title, items, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7 }}
      data-testid={`region-${testid}`}
    >
      <div className="flex items-center gap-3 mb-8">
        <Icon size={22} strokeWidth={1} className="text-[#112A46]" />
        <h3 className="font-serif text-3xl md:text-4xl text-[#112A46] font-light">{title}</h3>
      </div>

      <div className="space-y-10">
        {items.map((item, i) => (
          <div key={i} data-testid={`region-${testid}-item-${i}`} className="border-t border-[#112A46]/20 pt-6">
            {item.image && (
              <div className="mb-5 overflow-hidden aspect-[4/3]">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover kb-image" loading="lazy" />
              </div>
            )}
            <div className="font-serif text-2xl md:text-3xl text-[#112A46] font-light">{item.name}</div>
            {item.desc && <p className="mt-3 text-sm text-[#5C665F] leading-relaxed font-light">{item.desc}</p>}
          </div>
        ))}
      </div>
    </motion.div>
  );
}
